import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../lib/supabase.js";
import { callFunction } from "../lib/callFunction.js";
import { StickyTitle } from "../lib/StickyTitle.js";
import { INDUSTRIES, industryLabel, isKnownIndustry } from "../lib/industries.js";
import { HARD_SAFEGUARDS } from "../lib/safeguards.js";
import { Badge, Button, Card, EmptyState, Modal, Select } from "../lib/ui.js";
import { MAX_RULE_CHARS, RULE_READERS, SHARED_RULE_LIMITS, type RuleReaderValue } from "../lib/ruleLimits.js";

/** The same limits the database enforces for each group (packages/shared/rules/rules.ts, migration 0037). */
const MAX_RULES = SHARED_RULE_LIMITS.maxRules;
const MAX_TOTAL_CHARS = SHARED_RULE_LIMITS.maxTotalChars;
const MAX_INSTRUCTION_CHARS = 2000;

interface PlatformRule {
  id: string;
  scope: "global" | "industry";
  industry: string | null;
  fact: string;
  active: boolean;
  /** Which model reads it: both, the backend (decides and uses the tools), or the voice model (what it says and how). */
  audience: RuleReaderValue;
}

interface BusinessRow {
  id: string;
  name: string;
  industry: string | null;
}

interface BusinessRuleRow {
  id: string;
  fact: string;
  business_id: string;
}

/** One change the rule writer proposed, stored until the administrator approves or rejects it (platform_rule_proposals). */
interface Proposal {
  id: string;
  scope: "global" | "industry";
  industry: string | null;
  op: "add" | "update" | "delete";
  target_rule_id: string | null;
  fact: string;
  /** Who the agent decided should read it (both, backend, voice); approving passes it on to the rule. */
  audience: RuleReaderValue;
}

interface ExtractResult {
  proposals: Array<{ id: string }>;
  notRules: Array<{ text: string; reason: string }>;
}

const GLOBAL = "global";

function ruleErrorMessage(message: string): string {
  if (message.includes("rule_budget_exceeded")) return `This group is full (${MAX_RULES} rules, ${MAX_TOTAL_CHARS} characters). Turn off or shorten a rule first.`;
  if (message.includes("rule_text_invalid")) return `A rule needs to be between 8 and ${MAX_RULE_CHARS} characters.`;
  if (message.includes("industry_invalid")) return "That isn't a valid industry.";
  if (message.includes("rule_not_found")) return "That rule no longer exists.";
  if (message.includes("proposal_not_found")) return "That one was already dealt with.";
  if (message.includes("not_authorized")) return "Only a platform admin can change these.";
  return message || "That couldn't be completed.";
}

/**
 * Industry rules: instructions the receptionist follows for every business ("All businesses") or for one kind of business
 * (an industry), written by the platform admin in plain words. The agent turns what is typed into short rules, compared with
 * the ones already there; each change waits here for approval, and approved rules reach calls that start afterwards, ahead of
 * the business's own rules and under the same fixed safeguards. A business never sees this list. Rules can also be edited by
 * hand, switched off or deleted, so no rule needs a code change.
 */
export function IndustryRules() {
  const [scopeKey, setScopeKey] = useState<string>(GLOBAL);
  const [rules, setRules] = useState<PlatformRule[]>([]);
  const [businesses, setBusinesses] = useState<BusinessRow[]>([]);
  const [businessRules, setBusinessRules] = useState<BusinessRuleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [instruction, setInstruction] = useState("");
  const [working, setWorking] = useState(false);
  const [allProposals, setAllProposals] = useState<Proposal[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [notRules, setNotRules] = useState<Array<{ text: string; reason: string }>>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [editAudience, setEditAudience] = useState<RuleReaderValue>("both");
  const [deleteTarget, setDeleteTarget] = useState<PlatformRule | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement>(null);

  const load = useCallback(async () => {
    const [{ data: ruleRows }, { data: bizRows }, { data: ownRows }, { data: proposalRows }] = await Promise.all([
      supabase.from("platform_rules").select("id, scope, industry, fact, active, audience").order("created_at"),
      supabase.from("businesses").select("id, name, industry").order("name"),
      supabase.from("receptionist_rules").select("id, fact, business_id").eq("status", "active").order("created_at"),
      supabase.from("platform_rule_proposals").select("id, scope, industry, op, target_rule_id, fact, audience").order("created_at"),
    ]);
    setAllProposals((proposalRows ?? []) as Proposal[]);
    setRules((ruleRows ?? []) as PlatformRule[]);
    setBusinesses((bizRows ?? []) as BusinessRow[]);
    setBusinessRules((ownRows ?? []) as BusinessRuleRow[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // Industries to choose from: the known list, plus any other value a business already has.
  const options = useMemo(() => {
    const custom = [...new Set(businesses.map((b) => b.industry).filter((i): i is string => !!i && !isKnownIndustry(i)))];
    return [...INDUSTRIES.map((i) => ({ value: i.value as string, label: i.label })), ...custom.map((c) => ({ value: c, label: `${c} (custom)` }))];
  }, [businesses]);

  const scope: "global" | "industry" = scopeKey === GLOBAL ? "global" : "industry";
  const inScope = rules.filter((r) => (scope === "global" ? r.scope === "global" : r.scope === "industry" && r.industry === scopeKey));
  const activeInScope = inScope.filter((r) => r.active);
  const activeChars = activeInScope.reduce((n, r) => n + r.fact.length, 0);
  const audience = scope === "global" ? businesses : businesses.filter((b) => b.industry === scopeKey);
  const nameOf = new Map(businesses.map((b) => [b.id, b.name]));
  const suggestions = businessRules.filter((r) => {
    const b = businesses.find((x) => x.id === r.business_id);
    return scope === "global" ? !!b : b?.industry === scopeKey;
  });
  const scopeLabel = scope === "global" ? "All businesses" : industryLabel(scopeKey);
  const keyOf = (p: { scope: string; industry: string | null }) => (p.scope === "global" ? GLOBAL : (p.industry ?? ""));
  const proposals = allProposals.filter((p) => keyOf(p) === scopeKey);
  const waitingIn = (key: string) => allProposals.filter((p) => keyOf(p) === key).length;
  const waitingSuffix = (key: string) => (waitingIn(key) > 0 ? ` (${waitingIn(key)} waiting)` : "");
  const ruleText = new Map(rules.map((r) => [r.id, r.fact]));

  function changeScope(value: string) {
    setScopeKey(value);
    setNotRules([]);
    setError(null);
    setEditingId(null);
  }

  async function save(opts: { id: string | null; fact: string; active: boolean; scope?: "global" | "industry"; industry?: string | null; audience?: RuleReaderValue | null }) {
    setBusy(true);
    setError(null);
    const s = opts.scope ?? scope;
    const { error: err } = await supabase.rpc("rpc_admin_save_platform_rule", {
      p_id: opts.id,
      p_scope: s,
      p_industry: s === "global" ? null : (opts.industry ?? scopeKey),
      p_fact: opts.fact,
      p_active: opts.active,
      // null = keep who reads it on an edit, "both" on a new rule
      p_audience: opts.audience ?? null,
    });
    setBusy(false);
    if (err) {
      setError(ruleErrorMessage(err.message));
      return false;
    }
    await load();
    return true;
  }

  async function turnIntoRules() {
    if (!instruction.trim()) return;
    setWorking(true);
    setError(null);
    setNotRules([]);
    const res = await callFunction<ExtractResult>("platform-rule-extract", {
      scope,
      ...(scope === "industry" ? { industry: scopeKey } : {}),
      instruction: instruction.trim(),
    });
    setWorking(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setNotRules(res.data.notRules ?? []);
    if ((res.data.proposals ?? []).length > 0) setInstruction("");
    await load();
  }

  async function addAsWritten() {
    if (!instruction.trim()) return;
    if (await save({ id: null, fact: instruction, active: true })) setInstruction("");
  }

  /** Approves (applying it) or rejects (dropping it) a stored proposal; the wording can be corrected first. */
  async function decide(p: Proposal, approveIt: boolean): Promise<boolean> {
    setBusy(true);
    setError(null);
    const edited = drafts[p.id];
    const { error: err } = await supabase.rpc("rpc_admin_review_platform_proposal", {
      p_proposal_id: p.id,
      p_approve: approveIt,
      p_fact: approveIt && p.op !== "delete" && edited !== undefined && edited.trim() !== p.fact ? edited : null,
    });
    setBusy(false);
    if (err) setError(ruleErrorMessage(err.message));
    await load();
    return !err;
  }

  const approve = (p: Proposal) => decide(p, true);

  async function approveAll() {
    for (const p of proposals) if (!(await approve(p))) return;
  }

  async function saveEdit(rule: PlatformRule) {
    if (editText.trim() === rule.fact && editAudience === rule.audience) {
      setEditingId(null);
      return;
    }
    if (await save({ id: rule.id, fact: editText, active: rule.active, scope: rule.scope, industry: rule.industry, audience: editAudience })) setEditingId(null);
  }

  async function remove(rule: PlatformRule) {
    setBusy(true);
    const { error: err } = await supabase.rpc("rpc_admin_delete_platform_rule", { p_id: rule.id });
    setBusy(false);
    setDeleteTarget(null);
    if (err) setError(ruleErrorMessage(err.message));
    await load();
  }

  if (loading) return <p style={{ color: "var(--rc-text-muted)" }}>Loading...</p>;

  const editingRule = editingId ? (rules.find((x) => x.id === editingId) ?? null) : null;

  return (
    <div>
      <StickyTitle>
      <h1 className="rc-h1" style={{ marginBottom: 4 }}>Industry rules</h1>
      <p style={{ color: "var(--rc-text-muted)", fontSize: 13, margin: "0 0 16px" }}>
        Tell the receptionist how to behave, in your own words, for every business or for one kind of business. The agent turns it into short rules for you
        to approve. They come before each business's own rules, and they can't change the fixed safeguards below. Businesses never see this list.
      </p>

      <label htmlFor="rule-scope" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
        Rules for
      </label>
      <Select id="rule-scope" value={scopeKey} onChange={(e) => changeScope(e.target.value)} style={{ maxWidth: 360 }}>
        <option value={GLOBAL}>{`All businesses${waitingSuffix(GLOBAL)}`}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {`${o.label}${waitingSuffix(o.value)}`}
          </option>
        ))}
      </Select>
      </StickyTitle>
      <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 20px" }}>
        {scope === "global"
          ? `Applies to every business (${audience.length}).`
          : audience.length === 0
            ? `No business is set to ${scopeLabel} yet, so these rules apply to nobody. Set a business's industry on the Clients & businesses page.`
            : `Applies to ${audience.length} ${audience.length === 1 ? "business" : "businesses"}: ${audience.map((b) => b.name).join(", ")}.`}
      </p>

      <Card style={{ marginBottom: 20 }}>
        <label htmlFor="rule-instruction" style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
          What should the receptionist do for {scopeLabel}?
        </label>
        <textarea
          id="rule-instruction"
          ref={box}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value.slice(0, MAX_INSTRUCTION_CHARS))}
          rows={4}
          disabled={working}
          placeholder="In your own words. For example: when a caller doesn't say whether they want a men's or a women's cut, ask."
          style={textareaStyle}
        />
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, gap: 12, flexWrap: "wrap" }}>
          <button style={linkButton("var(--rc-primary)")} onClick={addAsWritten} disabled={busy || working || !instruction.trim()}>
            Add my wording exactly as written
          </button>
          <Button variant="primary" onClick={turnIntoRules} disabled={working || !instruction.trim()}>
            {working ? "Working it out..." : "Turn into rules"}
          </Button>
        </div>
        {error && <p style={{ color: "var(--rc-danger)", fontSize: 12.5, margin: "10px 0 0" }}>{error}</p>}
        {notRules.length > 0 && (
          <div style={{ marginTop: 12, background: "var(--rc-warning-soft)", border: "1px solid var(--rc-warning)", borderRadius: 8, padding: 12, fontSize: 13 }}>
            <strong>Not turned into a rule:</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {notRules.map((n, i) => (
                <li key={i}>{n.reason}</li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {proposals.length > 0 && (
        <section style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
            <h2 className="rc-h2" style={{ margin: "0 0 4px" }}>Waiting for your approval</h2>
            {proposals.length > 1 && (
              <button style={linkButton("var(--rc-primary)")} onClick={approveAll} disabled={busy}>
                Approve all
              </button>
            )}
          </div>
          <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 10px" }}>
            These stay here until you approve or reject them. Check each says what you meant; you can change the wording before approving.
          </p>
          {proposals.map((p) => (
            <Card key={p.id} style={{ marginBottom: 10 }}>
              <div style={{ marginBottom: 8, display: "flex", gap: 6 }}>
                <Badge tone={p.op === "delete" ? "danger" : p.op === "update" ? "warning" : "primary"}>
                  {p.op === "add" ? "New rule" : p.op === "update" ? "Change a rule" : "Remove a rule"}
                </Badge>
                {p.op === "add" && p.audience !== "both" && <Badge tone="neutral">{p.audience === "backend" ? "Backend only" : "Voice only"}</Badge>}
              </div>
              {p.op === "update" && p.target_rule_id && ruleText.get(p.target_rule_id) && (
                <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)", margin: "0 0 6px" }}>Now: {ruleText.get(p.target_rule_id)}</p>
              )}
              {p.op === "delete" ? (
                <p style={{ fontSize: 14, margin: "0 0 10px" }}>{(p.target_rule_id && ruleText.get(p.target_rule_id)) ?? p.fact}</p>
              ) : (
                <textarea
                  aria-label="Rule wording"
                  value={drafts[p.id] ?? p.fact}
                  onChange={(e) => setDrafts((d) => ({ ...d, [p.id]: e.target.value.slice(0, MAX_RULE_CHARS) }))}
                  rows={2}
                  style={{ ...textareaStyle, marginBottom: 10 }}
                />
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <Button variant={p.op === "delete" ? "danger" : "primary"} onClick={() => approve(p)} disabled={busy}>
                  {p.op === "delete" ? "Remove it" : "Approve"}
                </Button>
                <Button variant="secondary" onClick={() => decide(p, false)} disabled={busy}>
                  Reject
                </Button>
              </div>
            </Card>
          ))}
        </section>
      )}

      <h2 className="rc-h2" style={{ marginBottom: 4 }}>
        Rules in use for {scopeLabel}{" "}
        <span style={{ fontWeight: 400, fontSize: 12.5, color: "var(--rc-text-faint)" }}>
          ({activeInScope.length} of {MAX_RULES} rules, {activeChars} of {MAX_TOTAL_CHARS} characters)
        </span>
      </h2>
      {inScope.length === 0 ? (
        <EmptyState>No rules here yet.</EmptyState>
      ) : (
        inScope.map((r) => (
          <Card key={r.id} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <div>
                <p style={{ margin: 0, fontSize: 14, color: r.active ? "var(--rc-text)" : "var(--rc-text-faint)" }}>{r.fact}</p>
                {(!r.active || r.audience !== "both") && (
                  <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
                    {!r.active && <Badge tone="neutral">Off</Badge>}
                    {r.audience !== "both" && <Badge tone="primary">{r.audience === "backend" ? "Backend only" : "Voice only"}</Badge>}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 12, whiteSpace: "nowrap" }}>
                <button
                  style={linkButton("var(--rc-primary)")}
                  onClick={() => {
                    setEditText(r.fact);
                    setEditAudience(r.audience);
                    setEditingId(r.id);
                  }}
                >
                  Edit
                </button>
                <button
                  style={linkButton("var(--rc-primary)")}
                  disabled={busy}
                  onClick={() => save({ id: r.id, fact: r.fact, active: !r.active, scope: r.scope, industry: r.industry, audience: r.audience })}
                >
                  {r.active ? "Turn off" : "Turn on"}
                </button>
                <button style={linkButton("var(--rc-danger)")} onClick={() => setDeleteTarget(r)}>
                  Delete
                </button>
              </div>
            </div>
          </Card>
        ))
      )}

      <h2 className="rc-h2" style={{ margin: "24px 0 4px" }}>Rules businesses have written</h2>
      <p style={{ color: "var(--rc-text-muted)", fontSize: 12.5, margin: "0 0 10px" }}>
        {scope === "global" ? "From any business." : `From businesses set to ${scopeLabel}.`} To share one, use it as a starting point: the agent takes out anything
        specific to that business when it turns it into a rule.
      </p>
      {suggestions.length === 0 ? (
        <EmptyState>No business has written a rule here yet.</EmptyState>
      ) : (
        suggestions.map((s) => (
          <Card key={s.id} style={{ marginBottom: 8 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
              <div>
                <p style={{ margin: 0, fontSize: 14 }}>{s.fact}</p>
                <p style={{ margin: "4px 0 0", fontSize: 12, color: "var(--rc-text-faint)" }}>{nameOf.get(s.business_id) ?? "A business"}</p>
              </div>
              <button
                style={linkButton("var(--rc-primary)")}
                onClick={() => {
                  setInstruction(s.fact);
                  box.current?.focus();
                }}
              >
                Use as a starting point
              </button>
            </div>
          </Card>
        ))
      )}

      <h2 className="rc-h2" style={{ margin: "24px 0 4px" }}>Fixed safeguards</h2>
      <p style={{ color: "var(--rc-text-muted)", fontSize: 12.5, margin: "0 0 8px" }}>
        No rule, from you or from a business, can change these. The system enforces them whatever the receptionist is told.
      </p>
      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: "var(--rc-text)" }}>
        {HARD_SAFEGUARDS.map((s) => (
          <li key={s} style={{ marginBottom: 4 }}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </li>
        ))}
      </ul>

      {editingRule && (
        <Modal title="Edit rule" onClose={() => setEditingId(null)} width={640}>
          <label htmlFor="rule-edit-text" style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--rc-text-muted)", marginBottom: 6 }}>
            What the receptionist should do
          </label>
          <textarea
            id="rule-edit-text"
            autoFocus
            value={editText}
            onChange={(e) => setEditText(e.target.value.slice(0, MAX_RULE_CHARS))}
            rows={8}
            style={textareaStyle}
          />
          <div style={{ textAlign: "right", fontSize: 12, color: "var(--rc-text-faint)", margin: "4px 0 12px" }}>
            {editText.length} / {MAX_RULE_CHARS}
          </div>
          <label htmlFor="rule-edit-reader" style={{ display: "block", fontSize: 12.5, fontWeight: 600, color: "var(--rc-text-muted)", marginBottom: 6 }}>
            Read by
          </label>
          <Select id="rule-edit-reader" value={editAudience} onChange={(e) => setEditAudience(e.target.value as RuleReaderValue)} style={{ maxWidth: 360 }}>
            {RULE_READERS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          {error && <p style={{ color: "var(--rc-danger)", fontSize: 13, margin: "12px 0 0" }}>{error}</p>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button variant="secondary" onClick={() => setEditingId(null)} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={() => saveEdit(editingRule)} disabled={busy || editText.trim().length === 0}>
              {busy ? "Saving..." : "Save"}
            </Button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <Modal title="Delete this rule?" onClose={() => setDeleteTarget(null)} width={420}>
          <p style={{ fontSize: 13.5, marginTop: 0 }}>{deleteTarget.fact}</p>
          <p style={{ fontSize: 12.5, color: "var(--rc-text-muted)" }}>Calls that start after this stop following it. To keep it for later, use Turn off instead.</p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)}>
              Keep it
            </Button>
            <Button variant="danger" onClick={() => remove(deleteTarget)} disabled={busy}>
              Delete rule
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

const textareaStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: 8,
  fontSize: 14,
  fontFamily: "inherit",
  border: "1px solid var(--rc-border-strong)",
  borderRadius: "var(--rc-radius-md)",
  background: "var(--rc-surface)",
  color: "var(--rc-text)",
};

const linkButton = (color: string): React.CSSProperties => ({ background: "none", border: "none", padding: 0, color, cursor: "pointer", fontSize: 13 });
